import React, { useEffect, useRef, useState } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  connections: number[];
  energy: number;
}

const EmergenceVisualization: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<'chaos' | 'order' | 'emergence'>('chaos');
  const [particleCount, setParticleCount] = useState(50);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const initParticles = () => {
      particlesRef.current = Array.from({ length: particleCount }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        connections: [],
        energy: Math.random()
      }));
    };

    initParticles();

    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const particles = particlesRef.current;
      const connectionDistance = phase === 'chaos' ? 50 : phase === 'order' ? 100 : 150;
      const attractionForce = phase === 'chaos' ? 0 : phase === 'order' ? 0.01 : 0.02;

      particles.forEach((p, i) => {
        p.connections = [];

        particles.forEach((other, j) => {
          if (i === j) return;
          const dx = other.x - p.x;
          const dy = other.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            p.connections.push(j);

            if (phase !== 'chaos') {
              p.vx += (dx / dist) * attractionForce;
              p.vy += (dy / dist) * attractionForce;
            }
          }
        });

        p.x += p.vx;
        p.y += p.vy;

        if (phase === 'emergence') {
          p.vx *= 0.99;
          p.vy *= 0.99;
        } else {
          p.vx *= 0.98;
          p.vy *= 0.98;
        }

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        p.x = Math.max(0, Math.min(canvas.width, p.x));
        p.y = Math.max(0, Math.min(canvas.height, p.y));
      });

      particles.forEach((p, _i) => {
        p.connections.forEach(j => {
          const other = particles[j];
          const dx = other.x - p.x;
          const dy = other.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const alpha = 1 - (dist / connectionDistance);

          ctx.strokeStyle = phase === 'chaos'
            ? `rgba(100, 255, 100, ${alpha * 0.3})`
            : phase === 'order'
            ? `rgba(100, 150, 255, ${alpha * 0.5})`
            : `rgba(255, 100, 255, ${alpha * 0.7})`;
          ctx.lineWidth = phase === 'emergence' ? 2 : 1;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(other.x, other.y);
          ctx.stroke();
        });

        const size = phase === 'emergence' ? 4 + p.connections.length * 0.5 : 3;
        ctx.fillStyle = phase === 'chaos'
          ? '#00ff00'
          : phase === 'order'
          ? '#4488ff'
          : '#ff00ff';
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fill();

        if (phase === 'emergence' && p.connections.length > 5) {
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size + 5, 0, Math.PI * 2);
          ctx.stroke();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [phase, particleCount]);

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl mb-4 text-green-300">
            EMERGENCE: FROM CHAOS TO COMPLEXITY
          </h1>
          <p className="mb-4 text-green-500">
            Watch simple particles following basic rules create complex patterns.
            This mirrors how consciousness, organizations, and systems emerge from simple interactions.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <button
              onClick={() => setPhase('chaos')}
              className={`px-6 py-3 border-2 transition-all ${
                phase === 'chaos'
                  ? 'border-green-400 bg-green-900/30 text-green-300'
                  : 'border-green-700 text-green-600 hover:border-green-500'
              }`}
            >
              CHAOS
              <div className="text-xs mt-1">Independent agents, no rules</div>
            </button>

            <button
              onClick={() => setPhase('order')}
              className={`px-6 py-3 border-2 transition-all ${
                phase === 'order'
                  ? 'border-blue-400 bg-blue-900/30 text-blue-300'
                  : 'border-green-700 text-green-600 hover:border-green-500'
              }`}
            >
              ORDER
              <div className="text-xs mt-1">Weak attraction, simple patterns</div>
            </button>

            <button
              onClick={() => setPhase('emergence')}
              className={`px-6 py-3 border-2 transition-all ${
                phase === 'emergence'
                  ? 'border-pink-400 bg-pink-900/30 text-pink-300'
                  : 'border-green-700 text-green-600 hover:border-green-500'
              }`}
            >
              EMERGENCE
              <div className="text-xs mt-1">Complex patterns from simple rules</div>
            </button>
          </div>

          <div className="mb-4">
            <label className="block mb-2">Particle Count: {particleCount}</label>
            <input
              type="range"
              min="20"
              max="150"
              value={particleCount}
              onChange={(e) => setParticleCount(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        <canvas
          ref={canvasRef}
          className="w-full h-[500px] border-2 border-green-700 bg-black"
        />

      </div>
    </div>
  );
};

export default EmergenceVisualization;
